import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/adminApi";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";

const field = "w-full mt-1.5 rounded-lg bg-slate-50 border border-slate-300 px-3 py-2 text-sm text-slate-900";

export default function AdminAccelerator() {
  const [cfg, setCfg] = useState(null);
  const [dimensions, setDimensions] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [detail, setDetail] = useState(null);
  const [signups, setSignups] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/accelerator/config").then(r => { setCfg(r.data.config); setDimensions(r.data.dimensions); }).catch(() => toast.error("Could not load accelerator config"));
    adminApi.get("/admin/accelerator/stats").then(r => setStats(r.data)).catch(() => {});
    adminApi.get("/admin/accelerator/users").then(r => setUsers(r.data)).catch(() => {});
    adminApi.get("/admin/accelerator/workshop-signups").then(r => setSignups(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.put("/admin/accelerator/config", {
        archetypes: cfg.archetypes, fit: cfg.fit, target: cfg.target,
        validation_questions: cfg.validation_questions, disclaimer: cfg.disclaimer,
        cta_text: cfg.cta_text, cta_note: cfg.cta_note, workshop: cfg.workshop,
      });
      setCfg(data.config);
      toast.success("Accelerator configuration saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save configuration");
    } finally { setSaving(false); }
  };

  const openDetail = async (uid) => {
    try { const { data } = await adminApi.get(`/admin/accelerator/users/${uid}`); setDetail({ uid, ...data }); }
    catch { toast.error("Could not load this user's accelerator record"); }
  };

  if (!cfg) return <p className="text-slate-500" data-testid="admin-accelerator-loading">Loading accelerator settings…</p>;

  return (
    <div className="space-y-5" data-testid="admin-accelerator">
      {stats && (
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="accel-stats">
          {[["Profiles", stats.profiles], ["Capabilities", stats.capabilities], ["Confirmed", stats.confirmed_capabilities], ["Hypotheses", stats.hypotheses], ["Blueprints", stats.blueprints], ["Handoffs", stats.handoffs]].map(([label, value]) => (
            <div key={label} className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-4">
              <p className="text-xs text-slate-500">{label}</p>
              <p className="text-xl font-bold text-slate-900 mt-1">{value}</p>
            </div>
          ))}
        </div>
      )}

      <section className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5 space-y-4" data-testid="accel-config-archetypes">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Income archetypes</h2>
          <p className="text-sm text-slate-500 mt-1">Shown to users as categories. Disable an archetype to remove it from extraction and hypotheses.</p>
        </div>
        {cfg.archetypes.map((a, i) => (
          <div key={a.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid={`accel-archetype-${a.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Input value={a.name} onChange={e => setCfg(c => ({ ...c, archetypes: c.archetypes.map((x, j) => j === i ? { ...x, name: e.target.value } : x) }))} className="max-w-sm bg-slate-50 border-slate-300 text-slate-900" data-testid={`accel-archetype-name-${a.id}`} />
              <label className="inline-flex items-center gap-2 text-xs text-slate-500">
                <input type="checkbox" className="w-4 h-4" checked={a.active !== false} onChange={e => setCfg(c => ({ ...c, archetypes: c.archetypes.map((x, j) => j === i ? { ...x, active: e.target.checked } : x) }))} data-testid={`accel-archetype-active-${a.id}`} /> Active
              </label>
            </div>
            <textarea rows={2} value={a.description} onChange={e => setCfg(c => ({ ...c, archetypes: c.archetypes.map((x, j) => j === i ? { ...x, description: e.target.value } : x) }))} className={field} data-testid={`accel-archetype-desc-${a.id}`} />
          </div>
        ))}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5 space-y-4" data-testid="accel-config-fit">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Second-Income Fit Score</h2>
            <p className="text-sm text-slate-500 mt-1">A planning and fit indicator only — never a prediction of income or success. Switch scoring off to show users the qualitative dimensions without a number.</p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-800">
            <input type="checkbox" className="w-4 h-4" checked={!!cfg.fit.scoring_enabled} onChange={e => setCfg(c => ({ ...c, fit: { ...c.fit, scoring_enabled: e.target.checked } }))} data-testid="accel-scoring-toggle" /> Scoring enabled
          </label>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {dimensions.map(d => (
            <label key={d.key} className="block text-xs text-slate-500">{d.label}
              <Input type="number" min="0" max="100" value={cfg.fit.weights?.[d.key] ?? d.default_weight} onChange={e => setCfg(c => ({ ...c, fit: { ...c.fit, weights: { ...c.fit.weights, [d.key]: Number(e.target.value) } } }))} className="mt-1 bg-slate-50 border-slate-300 text-slate-900" data-testid={`accel-weight-${d.key}`} />
            </label>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5 space-y-4" data-testid="accel-config-content">
        <h2 className="text-lg font-bold text-slate-900">Targets, questions and wording</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs text-slate-500">Default validation target
            <Input type="number" min="1000" value={cfg.target.amount} onChange={e => setCfg(c => ({ ...c, target: { ...c.target, amount: Number(e.target.value) } }))} className="mt-1 bg-slate-50 border-slate-300 text-slate-900" data-testid="accel-target-amount" />
          </label>
          <label className="block text-xs text-slate-500">Currency
            <select value={cfg.target.currency} onChange={e => setCfg(c => ({ ...c, target: { ...c.target, currency: e.target.value } }))} className={field} data-testid="accel-target-currency">
              <option value="INR">INR</option><option value="USD">USD</option>
            </select>
          </label>
        </div>
        <label className="block text-xs text-slate-500">Validation questions (one per line)
          <textarea rows={4} value={(cfg.validation_questions || []).join("\n")} onChange={e => setCfg(c => ({ ...c, validation_questions: e.target.value.split("\n").filter(Boolean) }))} className={field} data-testid="accel-validation-questions" />
        </label>
        <label className="block text-xs text-slate-500">Disclaimer shown to users
          <textarea rows={3} value={cfg.disclaimer} onChange={e => setCfg(c => ({ ...c, disclaimer: e.target.value }))} className={field} data-testid="accel-disclaimer" />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs text-slate-500">CTA text
            <Input value={cfg.cta_text} onChange={e => setCfg(c => ({ ...c, cta_text: e.target.value }))} className="mt-1 bg-slate-50 border-slate-300 text-slate-900" data-testid="accel-cta-text" />
          </label>
          <label className="block text-xs text-slate-500">CTA note
            <Input value={cfg.cta_note} onChange={e => setCfg(c => ({ ...c, cta_note: e.target.value }))} className="mt-1 bg-slate-50 border-slate-300 text-slate-900" data-testid="accel-cta-note" />
          </label>
        </div>
        <Button onClick={save} disabled={saving} className="bg-sky-600 hover:bg-sky-500 text-white" data-testid="accel-save-config">{saving ? "Saving…" : "Save accelerator configuration"}</Button>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5 space-y-4" data-testid="accel-config-workshop">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Growth Workshop</h2>
            <p className="text-sm text-slate-500 mt-1">Promotional block shown to members at the end of Your 2nd Income Validator. The booking link is controlled here.</p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-800">
            <input type="checkbox" className="w-4 h-4" checked={cfg.workshop?.enabled !== false} onChange={e => setCfg(c => ({ ...c, workshop: { ...(c.workshop || {}), enabled: e.target.checked } }))} data-testid="accel-workshop-enabled" /> Shown to members
          </label>
        </div>
        <label className="block text-xs text-slate-500">Headline
          <Input value={cfg.workshop?.headline || ""} onChange={e => setCfg(c => ({ ...c, workshop: { ...(c.workshop || {}), headline: e.target.value } }))} className="mt-1 bg-slate-50 border-slate-300 text-slate-900" data-testid="accel-workshop-headline" />
        </label>
        <label className="block text-xs text-slate-500">Promotional copy
          <textarea rows={4} value={cfg.workshop?.body || ""} onChange={e => setCfg(c => ({ ...c, workshop: { ...(c.workshop || {}), body: e.target.value } }))} className={field} data-testid="accel-workshop-body" />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs text-slate-500">Button label
            <Input value={cfg.workshop?.cta_label || ""} onChange={e => setCfg(c => ({ ...c, workshop: { ...(c.workshop || {}), cta_label: e.target.value } }))} className="mt-1 bg-slate-50 border-slate-300 text-slate-900" data-testid="accel-workshop-cta-label" />
          </label>
          <label className="block text-xs text-slate-500">Booking link (leave empty to hide the button)
            <Input value={cfg.workshop?.url || ""} onChange={e => setCfg(c => ({ ...c, workshop: { ...(c.workshop || {}), url: e.target.value } }))} placeholder="https://" className="mt-1 bg-slate-50 border-slate-300 text-slate-900" data-testid="accel-workshop-url" />
          </label>
        </div>
        <Button onClick={save} disabled={saving} className="bg-sky-600 hover:bg-sky-500 text-white" data-testid="accel-save-workshop">{saving ? "Saving…" : "Save Growth Workshop settings"}</Button>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="accel-workshop-signups">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="font-bold text-slate-900">Members who clicked the booking link</h3>
            <span className="text-xs text-slate-500">{signups?.total_clicks || 0} clicks · {signups?.signups?.length || 0} members</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Warm leads — they opened the booking page from Your 2nd Income Validator.</p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500"><th className="py-2 pr-3">Member</th><th className="pr-3">Current role</th><th className="pr-3">Direction</th><th className="pr-3">Clicks</th><th className="pr-3">Last clicked</th></tr></thead>
              <tbody>
                {(signups?.signups || []).map(s => (
                  <tr key={s.user_id} className="border-t border-slate-200 text-slate-700" data-testid={`workshop-signup-${s.user_id}`}>
                    <td className="py-2.5 pr-3">{s.email || s.user_id}</td>
                    <td className="pr-3">{s.current_role || "—"}</td>
                    <td className="pr-3">{s.direction || "—"}</td>
                    <td className="pr-3">{s.clicks}</td>
                    <td className="pr-3">{new Date(s.last_at).toLocaleString()}</td>
                  </tr>
                ))}
                {!signups?.signups?.length && <tr><td colSpan={5} className="py-4 text-slate-500" data-testid="workshop-signups-empty">No one has clicked the booking link yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5" data-testid="accel-users">
        <h2 className="text-lg font-bold text-slate-900">Accelerator users</h2>
        <p className="text-sm text-slate-500 mt-1">Read-only. Confirmed capabilities and chosen directions belong to the user and cannot be edited here.</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500"><th className="py-2 pr-3">User</th><th className="pr-3">Role</th><th className="pr-3">Caps</th><th className="pr-3">Primary</th><th className="pr-3">Blueprint</th><th className="pr-3">Handoffs</th><th /></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} className="border-t border-slate-200 text-slate-700" data-testid={`accel-user-${u.user_id}`}>
                  <td className="py-2.5 pr-3">{u.email || u.user_id}</td>
                  <td className="pr-3">{u.current_role || "—"}</td>
                  <td className="pr-3">{u.confirmed}/{u.extracted}{u.rejected ? ` (${u.rejected} rejected)` : ""}</td>
                  <td className="pr-3">{u.primary?.title || "—"}</td>
                  <td className="pr-3">{u.blueprint ? `${u.blueprint.status} v${u.blueprint.version}` : "—"}</td>
                  <td className="pr-3">{u.handoffs}</td>
                  <td><button onClick={() => openDetail(u.user_id)} className="text-sky-700 text-xs" data-testid={`accel-user-view-${u.user_id}`}>View</button></td>
                </tr>
              ))}
              {!users.length && <tr><td colSpan={7} className="py-4 text-slate-500">No accelerator activity yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {detail && (
        <section className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5" data-testid="accel-user-detail">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-900">Record · {detail.uid}</h2>
            <button onClick={() => setDetail(null)} className="text-xs text-slate-500" data-testid="accel-detail-close">Close</button>
          </div>
          <div className="mt-3 grid gap-3 lg:grid-cols-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Capabilities</p>
              <ul className="mt-1.5 space-y-1 text-sm text-slate-700">{detail.capabilities.map(c => <li key={c.id}>{c.name} — <span className="text-slate-500">{c.user_status} · AI {c.ai_confidence}</span></li>)}</ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Hypotheses</p>
              <ul className="mt-1.5 space-y-1 text-sm text-slate-700">{detail.hypotheses.map(h => <li key={h.id}>{h.title} — <span className="text-slate-500">{h.status}{h.fit_view?.score != null ? ` · fit ${h.fit_view.score}` : ""}</span></li>)}</ul>
            </div>
            <div className="lg:col-span-2">
              <p className="text-xs uppercase tracking-wide text-slate-500">Blueprint & handoffs</p>
              <p className="mt-1.5 text-sm text-slate-700">{detail.blueprint ? `${detail.blueprint.offer} (v${detail.blueprint.version}, ${detail.blueprint.status})` : "No blueprint"}</p>
              <p className="mt-1 text-xs text-slate-500">{detail.handoffs.length} handoff(s) · {detail.events.length} recent events</p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
